/**
 * Projectiles and transient particle effects.
 *
 * The simulation emits cosmetic events (shot, impact, death) each tick. Those
 * events never feed back into game state, so this layer is free to use floats,
 * wall-clock time and randomness without any risk to determinism.
 */

import * as THREE from 'three/webgpu';
import { float, positionGeometry, texture, vec3 } from 'three/tsl';
import { QuadLayer, attr } from './instanced.js';
import { teamColor } from './art.js';
import { EVENT_DEATH, EVENT_IMPACT, EVENT_SHOT, EVENT_STRIDE } from '../game/eventLayout.js';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  r: number;
  g: number;
  b: number;
}

const MAX_PARTICLES = 3000;

export class EffectsLayer {
  readonly projectiles: QuadLayer;
  readonly particles: QuadLayer;
  private pool: Particle[] = [];
  private live = 0;

  constructor(particleTex: THREE.Texture) {
    this.projectiles = new QuadLayer({
      capacity: 256,
      attributes: { aPos: 2, aTeam: 3 },
      material: projectileMaterial(particleTex),
      renderOrder: 25,
    });

    this.particles = new QuadLayer({
      capacity: 1024,
      attributes: { aPos: 2, aSize: 1, aColor: 4 },
      material: particleMaterial(particleTex),
      renderOrder: 26,
    });

    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.pool.push({ x: 0, y: 0, vx: 0, vy: 0, life: 0, maxLife: 1, size: 1, r: 1, g: 1, b: 1 });
    }
  }

  addTo(scene: THREE.Scene): void {
    scene.add(this.projectiles.mesh, this.particles.mesh);
  }

  /** Reads this tick's cosmetic events and spawns the matching effects. */
  consumeEvents(events: Float32Array, count: number): void {
    for (let i = 0; i < count; i++) {
      const o = i * EVENT_STRIDE;
      const kind = events[o] | 0;
      const x = events[o + 1];
      const y = events[o + 2];
      switch (kind) {
        case EVENT_SHOT:
          this.burst(x, y, 4, 2.5, 0.12, 1.0, 0.85, 0.4, 0.35);
          break;
        case EVENT_IMPACT:
          this.burst(x, y, 14, 4.0, 0.35, 1.0, 0.6, 0.2, 0.55);
          break;
        case EVENT_DEATH:
          this.burst(x, y, 30, 5.5, 0.7, 0.9, 0.45, 0.15, 0.9);
          this.burst(x, y, 16, 3.0, 1.1, 0.25, 0.25, 0.28, 1.1);
          break;
        default:
          break;
      }
    }
  }

  private burst(
    x: number,
    y: number,
    n: number,
    speed: number,
    life: number,
    r: number,
    g: number,
    b: number,
    size: number,
  ): void {
    for (let i = 0; i < n && this.live < MAX_PARTICLES; i++) {
      const p = this.pool[this.live++];
      const a = Math.random() * Math.PI * 2;
      const s = speed * (0.35 + Math.random() * 0.65);
      p.x = x;
      p.y = y;
      p.vx = Math.cos(a) * s;
      p.vy = Math.sin(a) * s;
      p.maxLife = life * (0.6 + Math.random() * 0.6);
      p.life = p.maxLife;
      p.size = size * (0.7 + Math.random() * 0.6);
      p.r = r;
      p.g = g;
      p.b = b;
    }
  }

  update(dt: number, projectiles: Float32Array, projectileCount: number, stride: number): void {
    // Projectiles are simulation-owned, so they are simply mirrored.
    this.projectiles.reserve(projectileCount);
    const pPos = this.projectiles.array('aPos');
    const pTeam = this.projectiles.array('aTeam');
    for (let i = 0; i < projectileCount; i++) {
      const o = i * stride;
      const col = teamColor(projectiles[o + 3] | 0);
      pPos[i * 2] = projectiles[o];
      pPos[i * 2 + 1] = projectiles[o + 1];
      pTeam[i * 3] = col.r + 0.4;
      pTeam[i * 3 + 1] = col.g + 0.35;
      pTeam[i * 3 + 2] = col.b + 0.3;
    }
    this.projectiles.commit(projectileCount);

    // Particles are presentation-only and integrated against real time.
    this.particles.reserve(this.live);
    const qPos = this.particles.array('aPos');
    const qSize = this.particles.array('aSize');
    const qColor = this.particles.array('aColor');

    let out = 0;
    for (let i = 0; i < this.live; i++) {
      const p = this.pool[i];
      p.life -= dt;
      if (p.life <= 0) continue;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      // Drag, so bursts expand fast then settle.
      p.vx *= 1 - Math.min(1, 4 * dt);
      p.vy *= 1 - Math.min(1, 4 * dt);

      const t = p.life / p.maxLife;
      qPos[out * 2] = p.x;
      qPos[out * 2 + 1] = p.y;
      qSize[out] = p.size * (0.35 + t * 0.9);
      qColor[out * 4] = p.r;
      qColor[out * 4 + 1] = p.g;
      qColor[out * 4 + 2] = p.b;
      qColor[out * 4 + 3] = t * t;

      // Compact the pool in place: survivors move to the front.
      if (out !== i) {
        const dst = this.pool[out];
        this.pool[out] = p;
        this.pool[i] = dst;
      }
      out++;
    }
    this.live = out;
    this.particles.commit(out);
  }

  dispose(): void {
    this.projectiles.dispose();
    this.particles.dispose();
  }
}

function projectileMaterial(tex: THREE.Texture): THREE.NodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial();
  const pos = attr('aPos', 'vec2');
  const team = attr('aTeam', 'vec3');
  const size = float(0.42);

  material.positionNode = vec3(
    positionGeometry.x.mul(size).add(pos.x),
    positionGeometry.y.mul(size).sub(pos.y),
    float(0),
  );
  material.colorNode = team;
  material.opacityNode = texture(tex).r;
  material.transparent = true;
  material.depthWrite = false;
  material.depthTest = false;
  material.blending = THREE.AdditiveBlending;
  return material;
}

function particleMaterial(tex: THREE.Texture): THREE.NodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial();
  const pos = attr('aPos', 'vec2');
  const size = attr('aSize', 'float');
  const color = attr('aColor', 'vec4');

  material.positionNode = vec3(
    positionGeometry.x.mul(size).add(pos.x),
    positionGeometry.y.mul(size).sub(pos.y),
    float(0),
  );
  material.colorNode = color.rgb;
  material.opacityNode = texture(tex).r.mul(color.a);
  material.transparent = true;
  material.depthWrite = false;
  material.depthTest = false;
  material.blending = THREE.AdditiveBlending;
  return material;
}
